import Heading from "@/_components/template/Heading";
import SectionBlock from "@/_components/template/SectionBlock";
import SectionHeading from "@/_components/template/SectionHeading";
import { useToast } from "@/_components/toast/ToastProvider";
import { buildApiUrl } from "@/lib/contactApi";
import { usePageSeo } from "@/lib/seo";
import { useEffect, useState, type ReactNode } from "react";

type RequestType = "project" | "query" | "meeting" | "";
type Timeline = "asap" | "2-weeks" | "month" | "flexible" | "";

type FormState = {
  requestType: RequestType;
  name: string;
  email: string;
  company: string;
  timeline: Timeline;
  preferredContact: string;
  details: string;
  budget: string;
  services: string[];
  meetingType: string;
  availability: string;
  attendees: string;
  queryTopic: string;
};

type FieldErrorMap = Partial<Record<keyof FormState, string>>;

const requestTypeOptions = [
  {
    id: "project",
    title: "Project Booking",
    description:
      "For websites, dashboards, portfolios, product builds, or AI-based features.",
  },
  {
    id: "query",
    title: "Normal Query",
    description: "For questions, collaboration interest, quick guidance, or general contact.",
  },
  {
    id: "meeting",
    title: "Meeting Booking",
    description: "For discovery calls, discussion sessions, and planned meetings.",
  },
] as const;

const serviceOptions = [
  "Portfolio website",
  "Landing page",
  "Dashboard UI",
  "Full-stack product",
  "AI integration",
  "Performance improvement",
];

const defaultForm: FormState = {
  requestType: "",
  name: "",
  email: "",
  company: "",
  timeline: "",
  preferredContact: "",
  details: "",
  budget: "",
  services: [],
  meetingType: "",
  availability: "",
  attendees: "",
  queryTopic: "",
};

const typeSpecificDefaults: Pick<
  FormState,
  | "timeline"
  | "details"
  | "budget"
  | "services"
  | "meetingType"
  | "availability"
  | "attendees"
  | "queryTopic"
> = {
  timeline: "",
  details: "",
  budget: "",
  services: [],
  meetingType: "",
  availability: "",
  attendees: "",
  queryTopic: "",
};

function toggleValue(list: string[], value: string) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateForm(form: FormState): FieldErrorMap {
  const errors: FieldErrorMap = {};

  if (!form.requestType) errors.requestType = "Choose a request type first.";
  if (!form.name.trim()) errors.name = "Your name is required.";
  if (!form.email.trim()) errors.email = "Your email is required.";
  else if (!isValidEmail(form.email)) errors.email = "Enter a valid email address.";
  if (!form.preferredContact) errors.preferredContact = "Choose how you want the reply.";

  if (form.requestType === "project") {
    if (!form.timeline) errors.timeline = "Choose a timeline.";
    if (!form.budget) errors.budget = "Choose a budget range.";
    if (!form.services.length) errors.services = "Select at least one service.";
    if (!form.details.trim()) errors.details = "Add a short project brief.";
  }

  if (form.requestType === "query") {
    if (!form.queryTopic) errors.queryTopic = "Choose a query type.";
    if (!form.timeline) errors.timeline = "Choose how soon you need a reply.";
    if (!form.details.trim()) errors.details = "Write your query.";
  }

  if (form.requestType === "meeting") {
    if (!form.meetingType) errors.meetingType = "Choose a meeting type.";
    if (!form.availability.trim()) errors.availability = "Share your availability.";
    if (!form.details.trim()) errors.details = "Add the purpose of the meeting.";
  }

  return errors;
}

function buildPayload(form: FormState) {
  return {
    requestType: form.requestType,
    name: form.name.trim(),
    email: form.email.trim(),
    company: form.company.trim(),
    timeline: form.timeline,
    preferredContact: form.preferredContact,
    details: form.details.trim(),
    budget: form.budget,
    services: form.services,
    meetingType: form.meetingType,
    availability: form.availability.trim(),
    attendees: form.attendees.trim(),
    queryTopic: form.queryTopic,
  };
}

function OptionButton({
  active,
  title,
  description,
  onClick,
}: {
  active: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer rounded-[1.75rem] border p-5 text-left transition-all duration-300 hover:-translate-y-1 ${
        active
          ? "border-primary bg-surface shadow-[0_18px_50px_rgba(17,17,17,0.08)]"
          : "border-border bg-ground hover:border-primary/50"
      }`}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">
        {active ? "Selected" : "Select"}
      </p>
      <h3 className="mt-3 font-heading text-3xl text-primary sm:text-4xl">{title}</h3>
      <p className="mt-3 text-base leading-7 text-secondary sm:text-lg">{description}</p>
    </button>
  );
}

function InputField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-secondary">
        {label}
      </span>
      <div className="mt-2">{children}</div>
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = "text",
  hasError = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: string;
  hasError?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={`w-full rounded-[1rem] border bg-ground px-4 py-2.5 text-sm text-primary outline-none transition-colors placeholder:text-secondary/70 focus:border-primary sm:text-[15px] ${
        hasError ? "border-rose-500" : "border-border"
      }`}
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
  hasError = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  rows?: number;
  hasError?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={`w-full rounded-[1rem] border bg-ground px-4 py-2.5 text-sm text-primary outline-none transition-colors placeholder:text-secondary/70 focus:border-primary sm:text-[15px] ${
        hasError ? "border-rose-500" : "border-border"
      }`}
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
  placeholder,
  hasError = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  hasError?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`w-full rounded-[1rem] border bg-ground px-4 py-2.5 text-sm text-primary outline-none transition-colors focus:border-primary sm:text-[15px] ${
        hasError ? "border-rose-500" : "border-border"
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function ChipGroup({
  options,
  selected,
  onToggle,
  hasError = false,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  hasError?: boolean;
}) {
  return (
    <div
      className={`flex flex-wrap gap-2.5 rounded-[1rem] border p-3 ${
        hasError ? "border-rose-500" : "border-border"
      }`}
    >
      {options.map((option) => {
        const active = selected.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => onToggle(option)}
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-all duration-300 sm:text-[15px] ${
              active
                ? "border-primary bg-primary text-ground"
                : "border-border text-primary hover:border-primary"
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

function RevealPanel({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}) {
  if (!visible) return null;

  return <div className="animate-[onboarding-reveal_0.45s_ease-out]">{children}</div>;
}

export default function ContactOnboardingPage() {
  const [form, setForm] = useState<FormState>(defaultForm);
  const [errors, setErrors] = useState<FieldErrorMap>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { pushToast, updateToast } = useToast();
  usePageSeo({
    title: "Contact Tuhin Kairi | Project Booking, Queries and Meetings",
    description:
      "Book a project, send a query, or request a meeting with Tuhin Kairi. Use the guided contact page to share your goals, timeline, budget, and preferred response method.",
    keywords: [
      "contact Tuhin Kairi",
      "project booking",
      "freelance developer contact",
      "meeting booking",
      "web developer inquiry",
      "portfolio contact",
    ],
  });

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const chooseRequestType = (requestType: RequestType) => {
    setForm((prev) => ({
      ...prev,
      ...typeSpecificDefaults,
      requestType,
    }));
    setErrors({});
  };

  const goBackToSelection = () => {
    setForm((prev) => ({ ...prev, ...typeSpecificDefaults, requestType: "" }));
    setErrors({});
  };

  const resetForm = () => {
    setForm(defaultForm);
    setErrors({});
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      pushToast({
        title: "Check the form",
        description: "A few required fields still need your input before the request can be sent.",
        variant: "error",
      });
      return;
    }

    const toastId = pushToast({
      title: "Sending request",
      description: "Your contact request is being delivered now.",
      variant: "loading",
    });

    setIsSubmitting(true);

    try {
      const response = await fetch(buildApiUrl("/api/contact"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildPayload(form)),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const serverMessage =
          data?.errors?.[0]?.msg ||
          data?.message ||
          "The request could not be sent. Please try again.";

        throw new Error(serverMessage);
      }

      updateToast(toastId, {
        title: "Request sent",
        description:
          "Your message is in. You should receive a follow-up acknowledgement shortly.",
        variant: "success",
      });
      resetForm();
    } catch (error) {
      updateToast(toastId, {
        title: "Send failed",
        description:
          error instanceof Error
            ? error.message
            : "Something went wrong while sending the request.",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative tracking-wider sm:px-6">
      <div className="container mx-auto px-4 pt-28 pb-14 sm:px-5 sm:pt-32 lg:px-0 lg:pt-20">
        <SectionBlock className="gap-8 py-8" cols="1">
          {!form.requestType ? (
            <div className="space-y-6">
              <div className="space-y-6 border-b border-border pb-10 text-center lg:text-left">
                <SectionHeading
                  number="04"
                  content="Client Booking"
                  className="justify-center pb-2 lg:justify-start"
                />
                <Heading className="mx-auto max-w-[18ch] sm:max-w-full lg:mx-0">
                  Book a project, send a query, or request a meeting.
                </Heading>
                <p className="mx-auto max-w-[50ch] text-sm leading-7 text-secondary sm:max-w-3xl sm:text-base lg:mx-0 lg:text-lg">
                  Step 1: choose the kind of request. The next screen will show a simpler
                  form based on that choice.
                </p>
              </div>

              <div className="animate-[onboarding-reveal_0.45s_ease-out]">
                <div className="grid gap-4 md:grid-cols-3">
                  {requestTypeOptions.map((option) => (
                    <OptionButton
                      key={option.id}
                      active={form.requestType === option.id}
                      title={option.title}
                      description={option.description}
                      onClick={() => chooseRequestType(option.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="animate-[onboarding-reveal_0.45s_ease-out]">
              <button
                type="button"
                onClick={goBackToSelection}
                className="rounded-full border border-border bg-surface px-5 py-2.5 text-sm uppercase tracking-[0.18em] text-secondary transition-colors hover:border-primary hover:text-primary"
              >
                Back
              </button>
            </div>
          )}

          <div className="space-y-4">
            <RevealPanel visible={Boolean(form.requestType)}>
              <div className="rounded-[1.75rem] border border-border bg-surface p-5 sm:p-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <InputField label="Name" error={errors.name}>
                    <TextInput
                      value={form.name}
                      onChange={(value) => updateField("name", value)}
                      placeholder="Your name"
                      hasError={Boolean(errors.name)}
                    />
                  </InputField>
                  <InputField label="Email" error={errors.email}>
                    <TextInput
                      value={form.email}
                      onChange={(value) => updateField("email", value)}
                      placeholder="Your email"
                      type="email"
                      hasError={Boolean(errors.email)}
                    />
                  </InputField>
                  <InputField label="Company" error={errors.company}>
                    <TextInput
                      value={form.company}
                      onChange={(value) => updateField("company", value)}
                      placeholder="Company or brand"
                      hasError={Boolean(errors.company)}
                    />
                  </InputField>
                  <InputField
                    label="Preferred contact"
                    error={errors.preferredContact}
                  >
                    <SelectInput
                      value={form.preferredContact}
                      onChange={(value) => updateField("preferredContact", value)}
                      placeholder="How should I reply?"
                      hasError={Boolean(errors.preferredContact)}
                      options={[
                        { value: "email", label: "Email" },
                        { value: "linkedin", label: "LinkedIn" },
                        { value: "meeting-link", label: "Meeting link" },
                      ]}
                    />
                  </InputField>
                </div>
              </div>
            </RevealPanel>

            <RevealPanel visible={form.requestType === "query"}>
              <div className="rounded-[1.75rem] border border-border bg-ground p-5 sm:p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <InputField label="Query type" error={errors.queryTopic}>
                    <SelectInput
                      value={form.queryTopic}
                      onChange={(value) => updateField("queryTopic", value)}
                      placeholder="Select query type"
                      hasError={Boolean(errors.queryTopic)}
                      options={[
                        { value: "general", label: "General question" },
                        { value: "collaboration", label: "Collaboration inquiry" },
                        { value: "feedback", label: "Need feedback or review" },
                        { value: "other", label: "Other" },
                      ]}
                    />
                  </InputField>
                  <InputField label="Timeline" error={errors.timeline}>
                    <SelectInput
                      value={form.timeline}
                      onChange={(value) => updateField("timeline", value as Timeline)}
                      placeholder="When do you need a reply?"
                      hasError={Boolean(errors.timeline)}
                      options={[
                        { value: "asap", label: "As soon as possible" },
                        { value: "2-weeks", label: "Within two weeks" },
                        { value: "month", label: "Within a month" },
                        { value: "flexible", label: "Flexible" },
                      ]}
                    />
                  </InputField>
                </div>
                <div className="mt-4">
                  <InputField label="Your message" error={errors.details}>
                    <TextArea
                      value={form.details}
                      onChange={(value) => updateField("details", value)}
                      placeholder="Write your query here."
                      rows={3}
                      hasError={Boolean(errors.details)}
                    />
                  </InputField>
                </div>
              </div>
            </RevealPanel>

            <RevealPanel visible={form.requestType === "project"}>
              <div className="rounded-[1.75rem] border border-border bg-ground p-5 sm:p-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <InputField label="Timeline" error={errors.timeline}>
                    <SelectInput
                      value={form.timeline}
                      onChange={(value) => updateField("timeline", value as Timeline)}
                      placeholder="Select timeline"
                      hasError={Boolean(errors.timeline)}
                      options={[
                        { value: "asap", label: "As soon as possible" },
                        { value: "2-weeks", label: "Within two weeks" },
                        { value: "month", label: "Within a month" },
                        { value: "flexible", label: "Flexible" },
                      ]}
                    />
                  </InputField>
                  <InputField label="Budget" error={errors.budget}>
                    <SelectInput
                      value={form.budget}
                      onChange={(value) => updateField("budget", value)}
                      placeholder="Select budget range"
                      hasError={Boolean(errors.budget)}
                      options={[
                        { value: "under-1k", label: "Under $1k" },
                        { value: "1k-3k", label: "$1k - $3k" },
                        { value: "3k-8k", label: "$3k - $8k" },
                        { value: "8k-plus", label: "$8k+" },
                        { value: "discuss", label: "Let us discuss" },
                      ]}
                    />
                  </InputField>
                  <InputField label="Services needed" error={errors.services}>
                    <ChipGroup
                      options={serviceOptions}
                      selected={form.services}
                      onToggle={(value) =>
                        updateField("services", toggleValue(form.services, value))
                      }
                      hasError={Boolean(errors.services)}
                    />
                  </InputField>
                </div>
                <div className="mt-4">
                  <InputField label="Project brief" error={errors.details}>
                    <TextArea
                      value={form.details}
                      onChange={(value) => updateField("details", value)}
                      placeholder="Tell me about the project, goals, and what you want built."
                      rows={3}
                      hasError={Boolean(errors.details)}
                    />
                  </InputField>
                </div>
              </div>
            </RevealPanel>

            <RevealPanel visible={form.requestType === "meeting"}>
              <div className="rounded-[1.75rem] border border-border bg-ground p-5 sm:p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <InputField label="Meeting type" error={errors.meetingType}>
                    <SelectInput
                      value={form.meetingType}
                      onChange={(value) => updateField("meetingType", value)}
                      placeholder="Select meeting type"
                      hasError={Boolean(errors.meetingType)}
                      options={[
                        { value: "discovery", label: "Discovery call" },
                        { value: "project-discussion", label: "Project discussion" },
                        { value: "consultation", label: "Consultation" },
                        { value: "other", label: "Other" },
                      ]}
                    />
                  </InputField>
                  <InputField label="Attendees" error={errors.attendees}>
                    <TextInput
                      value={form.attendees}
                      onChange={(value) => updateField("attendees", value)}
                      placeholder="Who will join?"
                      hasError={Boolean(errors.attendees)}
                    />
                  </InputField>
                </div>
                <div className="mt-4">
                  <InputField label="Availability" error={errors.availability}>
                    <TextArea
                      value={form.availability}
                      onChange={(value) => updateField("availability", value)}
                      placeholder="Share the day and time that work for you."
                      rows={3}
                      hasError={Boolean(errors.availability)}
                    />
                  </InputField>
                </div>
                <div className="mt-4">
                  <InputField label="Meeting note" error={errors.details}>
                    <TextArea
                      value={form.details}
                      onChange={(value) => updateField("details", value)}
                      placeholder="Add the purpose of the meeting."
                      rows={3}
                      hasError={Boolean(errors.details)}
                    />
                  </InputField>
                </div>
              </div>
            </RevealPanel>

            <RevealPanel visible={Boolean(form.requestType)}>
              <div className="rounded-[1.75rem] border border-border bg-surface p-5 sm:p-6">
                <div className="flex flex-wrap gap-4">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="rounded-full border border-primary bg-primary px-6 py-3 text-sm uppercase tracking-[0.18em] text-ground transition-colors hover:bg-transparent hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? "Sending..." : "Send request"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="rounded-full border border-border px-6 py-3 text-sm uppercase tracking-[0.18em] text-secondary transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </RevealPanel>
          </div>
        </SectionBlock>
      </div>
    </section>
  );
}
